import AccordionTsoPq from '@components/Accordion/AccordionTsoPq';
import TextFieldTsoPq from '@components/TextField/TextFieldTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import {
  StyledAccordionContainer,
  StyledAccordionWrapper,
  StyledApplyButton,
  StyledTableContainer,
  StyledTableContainerHeaderWrapper,
} from '@pages/TrendIdentification/TrendIdentificationStyles';
import {
  ApiApplicationServicesDataTypesTrendTypesTrendResultsCount,
  useLazyGetTrendGetTrendResultsCountQuery,
} from '@redux/apiSlices/trendApi';
import { useAppSelector } from '@redux/hooks';
import {
  clearAllTrendChartData,
  selectActiveTrend,
  setDidUserApplyFilters,
  setIsFiltering,
  setSelectedCells,
  setTredData,
} from '@redux/slices/trendIdentificationSlice';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

type ConfigurationParametersProps = {
  averageIncreasePerYear: boolean;
  absoluteIncrease: boolean;
  currentValue: boolean;
  durationOfIncreaseInWeeks: boolean;
};

const TrendTable = ({ noHeader = false }) => {
  const [averageIncreasePerYear, setAverageIncreasePerYear] = useState<string>('0.2');
  const [absoluteIncrease, setAbsoluteIncrease] = useState<string>('');
  const [currentValue, setCurrentValue] = useState<string>('');
  const [durationOfIncreaseInWeeks, setDurationOfIncreaseInWeeks] = useState<string>('');

  const [validations, setValidations] = useState<ConfigurationParametersProps>({
    averageIncreasePerYear: true,
    absoluteIncrease: true,
    currentValue: true,
    durationOfIncreaseInWeeks: true,
  });

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [triggerGetTrendValues] = useLazyGetTrendGetTrendResultsCountQuery();
  const activeTrend = useAppSelector(selectActiveTrend);

  const floatValidation = (value: string) => {
    const number = Number(value);
    const isFloat = !Number.isNaN(number) && Number.isFinite(number);
    return isFloat;
  };

  const handleAverageIncreasePerYearChange = (value: string) => {
    setAverageIncreasePerYear(value);
    setValidations({ ...validations, averageIncreasePerYear: floatValidation(value) });
  };

  const handleAbsoluteIncreaseChange = (value: string) => {
    setAbsoluteIncrease(value);
    setValidations({ ...validations, absoluteIncrease: floatValidation(value) });
  };

  const handleCurrentValueChange = (value: string) => {
    setCurrentValue(value);
    setValidations({ ...validations, currentValue: floatValidation(value) });
  };

  const handleDurationOfIncreaseInWeeksChange = (value: string) => {
    setDurationOfIncreaseInWeeks(value);
    setValidations({ ...validations, durationOfIncreaseInWeeks: floatValidation(value) });
  };

  const getAllFilteredSelectedTrendValues = async () => {
    dispatch(setIsFiltering(true));

    try {
      const response = await triggerGetTrendValues(
        {
          trendJobId: activeTrend.id,
          valueThreshold: Number(currentValue),
          increaseThreshold: Number(absoluteIncrease),
          increaseDurationThreshold: Number(durationOfIncreaseInWeeks),
          averageIncreaseThreshold: Number(averageIncreasePerYear),
        },
        true
      );
      dispatch(setTredData(response.data as ApiApplicationServicesDataTypesTrendTypesTrendResultsCount[]));
    } catch (error) {
      console.error(error);
    } finally {
      dispatch(setIsFiltering(false));
    }
  };

  const handleFilter = () => {
    getAllFilteredSelectedTrendValues();
    dispatch(setDidUserApplyFilters(true));
    dispatch(setSelectedCells([]));
    dispatch(clearAllTrendChartData());
  };

  return (
    <StyledTableContainer>
      {!noHeader && (
        <StyledTableContainerHeaderWrapper>
          <TypographyTsoPq typographyType="sectionTitleMDBold">
            {t('trednIdentification.tableContainer.title')}
          </TypographyTsoPq>
          <TypographyTsoPq typographyType="bodyTextSM">
            {t('trednIdentification.tableContainer.description')}
          </TypographyTsoPq>
        </StyledTableContainerHeaderWrapper>
      )}

      <StyledAccordionWrapper>
        <AccordionTsoPq
          defaultExpanded={true}
          title={t('trednIdentification.tableContainer.parameterConfiguration.title')}
          content={
            <StyledAccordionContainer>
              <TextFieldTsoPq
                label={t('trednIdentification.tableContainer.parameterConfiguration.averageIncreasePerYear.label')}
                error={!validations.averageIncreasePerYear}
                value={averageIncreasePerYear}
                handleChange={(e) => handleAverageIncreasePerYearChange(e)}
                supportingText={
                  validations.averageIncreasePerYear
                    ? ''
                    : t('trednIdentification.tableContainer.parameterConfiguration.averageIncreasePerYear.error')
                }
                placeholder={t('trednIdentification.tableContainer.parameterConfiguration.placeholder')}
                customwidth="15.75rem"
                whiteBackground
              />
              <TextFieldTsoPq
                label={t('trednIdentification.tableContainer.parameterConfiguration.absoluteIncrease.label')}
                error={!validations.absoluteIncrease}
                value={absoluteIncrease}
                handleChange={(e) => handleAbsoluteIncreaseChange(e)}
                supportingText={
                  validations.absoluteIncrease
                    ? ''
                    : t('trednIdentification.tableContainer.parameterConfiguration.absoluteIncrease.error')
                }
                placeholder={t('trednIdentification.tableContainer.parameterConfiguration.placeholder')}
                customwidth="15.75rem"
                whiteBackground
              />
              <TextFieldTsoPq
                label={t('trednIdentification.tableContainer.parameterConfiguration.currentValue.label')}
                error={!validations.currentValue}
                value={currentValue}
                handleChange={(e) => handleCurrentValueChange(e)}
                supportingText={
                  validations.currentValue
                    ? ''
                    : t('trednIdentification.tableContainer.parameterConfiguration.currentValue.error')
                }
                placeholder={t('trednIdentification.tableContainer.parameterConfiguration.placeholder')}
                customwidth="15.75rem"
                whiteBackground
              />
              <TextFieldTsoPq
                label={t('trednIdentification.tableContainer.parameterConfiguration.durationOfIncreaseInWeeks.label')}
                error={!validations.durationOfIncreaseInWeeks}
                value={durationOfIncreaseInWeeks}
                handleChange={(e) => handleDurationOfIncreaseInWeeksChange(e)}
                supportingText={
                  validations.durationOfIncreaseInWeeks
                    ? ''
                    : t('trednIdentification.tableContainer.parameterConfiguration.durationOfIncreaseInWeeks.error')
                }
                placeholder={t('trednIdentification.tableContainer.parameterConfiguration.placeholder')}
                customwidth="15.75rem"
                whiteBackground
              />
              <StyledApplyButton
                buttonType="filledPrimary"
                textContent={t('trednIdentification.tableContainer.parameterConfiguration.apply')}
                onClick={handleFilter}
                hasError={
                  !validations.durationOfIncreaseInWeeks ||
                  !validations.currentValue ||
                  !validations.absoluteIncrease ||
                  !validations.averageIncreasePerYear
                }
              />
            </StyledAccordionContainer>
          }
        />
      </StyledAccordionWrapper>
    </StyledTableContainer>
  );
};

export default TrendTable;
